<template>
  <mi-btn-dropdown
    flat
    small
    :label="`TAR filter ${ selectedOptionsLengthString }`"
    class="q-ml-md"
    @click="openDropdown"
  >
    <div class="dropdown-wrapper">
      <mi-text-field
        v-model="searchString"
        placeholder="Search for a KV"
        class=" q-pa-sm"
        outlined
      >
      </mi-text-field>
      <div class="q-pa-md">
        <mi-infinite-scroll>
          <div v-if="kvListBySearch.length > 0">
            <mi-checkbox
              v-if="kvListBySearch.length === kvList.length"
              v-model="tempSelectAll"
              class="q-mb-md select-all-checkbox"
            >
              Select All
            </mi-checkbox>
            <mi-list v-for="(kv,index) in kvListBySearch" :key="`${ kv.name }_${ index }`">
              <mi-list-item class="q-pl-none q-pb-sm">
                <mi-checkbox v-model="kv.value">{{ kv.name }}</mi-checkbox>
              </mi-list-item>
            </mi-list>
          </div>
          <div v-else>
            <span>No found</span>
          </div>
        </mi-infinite-scroll>
      </div>
      <div class="q-ma-sm">
        <mi-btn v-close-popup flat color="accent" class="action-btn" @click="clearAll">Clear</mi-btn>
        <mi-btn v-close-popup color="accent" class="action-btn" @click="filterTarButton">Filter</mi-btn>
      </div>
    </div>
  </mi-btn-dropdown>
</template>

<script>
  import { computed, inject, ref, onMounted } from 'vue'
  import cloneDeep from 'lodash.clonedeep'
  import MiTextField from '../../../../packages/@mi-library/MiTextField/MiTextField.vue'
  import MiCheckbox from '../../../../packages/@mi-library/MiCheckbox/MiCheckbox.vue'
  import MiList from '../../../../packages/@mi-library/MiList/MiList.vue'
  import MiListItem from '../../../../packages/@mi-library/MiList/MiListItem.vue'
  import MiInfiniteScroll from '../../../../packages/@mi-library/MiInfiniteScroll/MiInfiniteScroll.vue'
  import { getComponent } from '../../../../api'

  export default {
    name: 'BomValidationResultsTarFiltering',
    components: { MiInfiniteScroll, MiListItem, MiList, MiCheckbox, MiTextField },
    props: {
      validationId: {
        type: String,
        required: false,
        default: ''
      },
      productModelId: {
        type: String,
        required: false,
        default: ''
      },
      pmType: {
        type: String,
        required: false,
        default: ''
      },
      componentId: {
        type: String,
        required: false,
        default: ''
      },
      filterResults: {
        type: Function,
        required: false,
        default: () => {}
      }
    },
    emits: ['update:kvsList'],
    setup(props, { emit }) {
      const searchString = ref('')
      const selectAll = ref(false)
      const ELEMENT_TYPES = {
        INCOMPLETE: 'INCOMPLETE'
      }

      const isFiltering = inject('isFilterTarLoading')

      const kvList = ref([])
      const tempKvList = ref([])

      const selectedOptionsLengthString = ref('')

      const kvListBySearch = computed(
        () => tempKvList.value.filter(kv => String(kv.name || '')
          .toLowerCase().includes(searchString.value.trim().toLowerCase()))
      )

      const setSelectedOptionsLengthString = () => {
        const size = kvList.value.filter(kv => kv.value).length
        selectedOptionsLengthString.value = size > 0 ? `(${ size })` : ''
      }

      const tempSelectAll = computed({
        get() {
          return tempKvList.value.length > 0 && tempKvList.value.every(kv => kv.value)
        },
        set(val) {
          if (val) {
            tempKvList.value.forEach(kv => {
              kv.value = val
            })
          }
          else if (tempKvList.value.filter(kv => kv.value).length === tempKvList.value.length) {
            tempKvList.value.forEach(kv => {
              kv.value = false
            })
          }
        }
      })

      const openDropdown = () => {
        searchString.value = ''
        tempKvList.value = cloneDeep(kvList.value)
        tempSelectAll.value = selectAll.value
      }

      const buildParams = () => {
        const params = new URLSearchParams()
        params.append('elementTypes', ELEMENT_TYPES.INCOMPLETE)
        return params
      }

      const filterTar = async () => {
        kvList.value = cloneDeep(tempKvList.value)

        const kvsIds = kvList.value.filter(kv => kv.value).map(kv => kv.id)

        const params = buildParams()

        await props.filterResults(props.validationId, params, kvsIds)

        emit('update:kvsList', kvsIds)
        setSelectedOptionsLengthString()
      }

      const clearAll = async () => {
        isFiltering.value = true

        tempKvList.value.forEach(kv => {
          kv.value = false
        })

        await filterTar()

        tempSelectAll.value = false
        selectAll.value = tempSelectAll.value
        searchString.value = ''

        isFiltering.value = false
      }

      const filterTarButton = async () => {
        isFiltering.value = true
        await filterTar()
        selectAll.value = tempSelectAll.value
        isFiltering.value = false
      }

      const fetchComponentVariants = async () => getComponent({
        id: props.componentId,
        pmType: props.pmType,
        pmIdentifier: props.productModelId,
        isId2: true
      })

      const updateKvList = variants => variants.map(kv => ({
        value: false,
        name: `${ kv.id2 } - ${ kv.name }`,
        id2: kv.id2,
        id: kv.id
      }))

      const initializeComponent = async () => {
        const result = await fetchComponentVariants()
        kvList.value = updateKvList(result.componentVariants)
        tempKvList.value = cloneDeep(kvList.value)
      }

      onMounted(async () => {
        try {
          await initializeComponent()
        }
        catch (error) {
          console.error('Failed to fetch component variants:', error)
        }
      })

      return {
        searchString,
        selectAll,
        kvList,
        tempKvList,
        kvListBySearch,
        clearAll,
        filterTarButton,
        selectedOptionsLengthString,
        openDropdown,
        buildParams,
        tempSelectAll,
        filterTar,
        setSelectedOptionsLengthString
      }
    }
  }
</script>

<style scoped lang="scss">
  .mi-btn-dropdown {
    width: 103px;
    align-self: center;
  }

  ::v-deep(.mi-checkbox) {
    .q-checkbox__label {
      padding-left: 10px;
    }

    .q-checkbox__inner {
      margin-bottom: auto;
    }
  }

  .mi-infinite-scroll {
    overflow: auto;
    height: calc(100vh - 500px);
    overflow-y: scroll;
  }

  .action-btn {
    width: 50%;
  }

  .dropdown-wrapper {
    width: 323px;
    height: 556px;
  }
</style>
